/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import queryString from 'query-string';

import { BOOTSTRAP_FAILURE, BOOTSTRAP_SUCCESS } from 'actions/app';
import { AUTH_SUCCESS, AUTH_FAILURE, AUTH_CLEAR } from 'actions/auth';

import {
  UPDATE_PROFILE_SUCCESS,
  REQUEST_CODE_SUCCESS,
  VERIFY_CODE_SUCCESS,
  RESET_CODE_SUCCESS,
} from 'actions/user';

import {
  REGISTER_COMPANY_SUCCESS,
  UPDATE_COMPANY_SUCCESS,
} from 'actions/company';

import {
  REGISTER_FREELANCE_SUCCESS,
  REGISTER_FREELANCE_COMPANY_SUCCESS,
  UPDATE_FREELANCE_COMPANY_SUCCESS,
} from 'actions/paymentMethod';

import { storage } from 'utils/storage';

const token = storage.read('token') || '';
const impersonation_token = storage.read('impersonation_token') || '';
const guest_token = storage.read('guest_token') || '';
const email = storage.read('email') || '';
const password_hash = storage.read('password_hash') || '';
const facebook_token = storage.read('facebook_token') || '';
const facebook_login_id = storage.read('facebook_login_id') || '';
const google_token = storage.read('google_token') || '';
const google_login_id = storage.read('google_login_id') || '';

const query = queryString.parse(location.search);
const asUser = query && query.as_user ? query.as_user : '';

/**
 * User authentification model.
 *
 * @type {object} auth                    The authenticate object for a user.
 * @type {string} auth.facebook_token     FB access token obtained via the FB Login button.
 * @type {string} auth.facebook_login_id  FB ID of user via login flow.
 * @type {string} auth.linkedin_token     Linkedin access token obtained via the Linkedin Login widget.
 * @type {string} auth.linkedin_login_id  Linkedin’s own user ID for that person.
 * @type {string} auth.google_token       Google access token obtained via the Google Login widget.
 * @type {string} auth.google_login_id    Google’s own user ID for that person.
 * @type {string} auth.language           Preferred language to use when communicating with this user (as an ISO 639-1 code).
 * @type {enum}   auth.signup_as          [CLIENT, TALENT], If user is signing up, whether doing so as a Talent or Client.
 */

const initialState = {
  isAuthenticated: !!token,
  token: token || guest_token,
  impersonation_token: impersonation_token,
  asUser,
  provider: {
    email,
    password_hash,
    facebook_token,
    facebook_login_id,
    google_token,
    google_login_id,
  },
};

/**
 * This is the auth reducer. It listens for actions and updates the auth object.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
export default function(state = initialState, { type, payload }) {
  switch (type) {
    case AUTH_SUCCESS:
      // Save impersonation token to special storage
      if (payload.is_impersonated) {
        storage.write('impersonation_token', payload.token);
      }

      storage.write('token', payload.token);
      storage.destroy('guest_token');

      // Fugly patch to identify user with Intercom
      console.log(
        `Identifying user with Intercom: ${payload.profile.fullname} ${payload.profile.email}`
      );

      window.intercomSettings = {
        app_id: 'biy44qrw',
        name: payload.profile.fullname,
        email: payload.profile.email,
      };

      (function() {
        var w = window;
        var ic = w.Intercom;
        if (typeof ic === 'function') {
          ic('reattach_activator');
          ic('update', intercomSettings);
        } else {
          var d = document;
          var i = function() {
            i.c(arguments);
          };
          i.q = [];
          i.c = function(args) {
            i.q.push(args);
          };
          w.Intercom = i;
          function l() {
            var s = d.createElement('script');
            s.type = 'text/javascript';
            s.async = true;
            s.src = 'https://widget.intercom.io/widget/biy44qrw';
            var x = d.getElementsByTagName('script')[0];
            x.parentNode.insertBefore(s, x);
          }
          if (w.attachEvent) {
            w.attachEvent('onload', l);
          } else {
            w.addEventListener('load', l, false);
          }
        }
      })();
      // End of fugly intercom patch

      return {
        ...state,
        isAuthenticated: true,
        token: payload.token,
        impersonation_token: payload.token,
      };

    case BOOTSTRAP_SUCCESS:
      storage.write('guest_token', payload.token);

      return {
        ...state,
        token: payload.token,
        impersonation_token: payload.token,
      };

    case UPDATE_PROFILE_SUCCESS:
    case REGISTER_COMPANY_SUCCESS:
    case UPDATE_COMPANY_SUCCESS:
    case REQUEST_CODE_SUCCESS:
    case VERIFY_CODE_SUCCESS:
    case RESET_CODE_SUCCESS:
    case REGISTER_FREELANCE_SUCCESS:
    case REGISTER_FREELANCE_COMPANY_SUCCESS:
    case UPDATE_FREELANCE_COMPANY_SUCCESS:
      storage.write('token', payload.token);
      storage.destroy('guest_token');

      return {
        ...state,
        isAuthenticated: true,
        token: payload.token,
        impersonation_token: payload.token,
      };

    case AUTH_FAILURE:
    case AUTH_CLEAR:
    case BOOTSTRAP_FAILURE:
      storage.destroy('token');
      storage.destroy('impersonation_token');
      storage.destroy('guest_token');
      storage.destroy('email');
      storage.destroy('password_hash');
      storage.destroy('facebook_token');
      storage.destroy('facebook_login_id');
      storage.destroy('google_login_id');
      storage.destroy('google_token');

      return {
        ...state,
        isAuthenticated: false,
        token: '',
        impersonation_token: '',
        asUser: '',
      };

    default:
      return state;
  }
}
